/*
  The rotator of the slideshow (sections/slideshow.liquid), the customer quotes (sections/testimonials.liquid),
  the announcement bar (sections/announcement-bar.liquid) and the complementary products
  (snippets/product-complementary.liquid). The slides are a native scroll-snap row (swipe and trackpad need no
  code); this adds the controls, the keyboard, looping, the transitions and the automatic rotation.

  Markup it reads: .slide-rotator__viewport holding the .slide-rotator__slide elements; the controls of
  snippets/slide-rotator-controls.liquid: [data-step] buttons, the pager's .pager__mark buttons and its counter, and the
  .slide-rotator__timer.

  Transitions (Theme settings › Animations › Slideshows; the announcement bar always fades): the row scrolls to the new
  slide, from the last one back to the first too; with data-fade, the slide on screen fades out and the new one fades
  in, on the theme's motion tokens (motionTokens in assets/global.js).

  Rotation: the slide's time is a CSS animation on .slide-rotator__timer (assets/component-slide-rotator.css), and its end moves
  on to the next slide. data-run is "playing", or "paused" when the device asks for less motion and, for good, once the
  shopper moves the slides themselves: a click or a tap inside the rotator, a swipe to another slide, an arrow key.
  data-hold stops the clock for a moment: the mouse is over the section, focus is inside the rotator, it is off screen,
  the tab is hidden, or the theme editor is showing a slide. The time that is left is kept. A timed pager
  (snippets/pager.liquid) gets the same two attributes, so its current dot fills and freezes with the clock.
*/

if (!customElements.get('slide-rotator')) {
  class SlideRotator extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.slide-rotator__viewport');
      this.slides = Array.from(this.querySelectorAll('.slide-rotator__slide'));
      if (!this.viewport || this.slides.length < 2) return;

      this.pages = Array.from(this.querySelectorAll('.pager__mark'));
      this.counters = this.querySelectorAll('[data-pager-current]');
      this.pager = this.querySelector('.pager[data-timed]');
      this.timer = this.querySelector('.slide-rotator__timer');
      this.index = 0;
      this.moving = false;
      this.holds = new Set();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.viewport.setAttribute('tabindex', '0');
      this.viewport.setAttribute('role', 'group');
      this.viewport.setAttribute('aria-label', this.dataset.labelSlides || '');
      this.update();

      // scrollend (MDN: Element scrollend event) fires once a swipe, a key or a smooth scroll has come to rest.
      this.viewport.addEventListener('scrollend', () => this.settle(), { signal });
      this.viewport.addEventListener('keydown', (event) => this.onKey(event), { signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      window.addEventListener('resize', () => this.align(), { passive: true, signal });

      if (this.hasAttribute('data-autoplay')) this.startRotation(signal);
      this.listenToEditor(signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.fading?.cancel();
    }

    // ---- Showing a slide ----

    /** Scrolls to a slide without any animation. */
    jump(index) {
      this.viewport.scrollTo({ left: index * this.viewport.clientWidth, behavior: 'instant' });
    }

    align() {
      if (!this.moving) this.jump(this.index);
    }

    /** The position of the slide the row is scrolled to. */
    onScreen() {
      return Math.round(this.viewport.scrollLeft / (this.viewport.clientWidth || 1));
    }

    go(target, { instant = false } = {}) {
      const count = this.slides.length;
      const next = (target + count) % count;
      if (next === this.index && !instant) return this.restartClock();
      this.index = next;
      this.update();
      this.restartClock();

      if (instant || lessMotion.matches) {
        this.fading?.cancel();
        this.moving = false;
        return this.jump(next);
      }
      // While the row moves or the slide on screen fades out, a resize leaves the row where it is.
      this.moving = true;
      if (this.hasAttribute('data-fade')) return this.fade();
      this.viewport.scrollTo({ left: next * this.viewport.clientWidth, behavior: 'smooth' });
    }

    /**
     * Fades the slide on screen out, from the strength it has (a fade still under way stops there), then puts the
     * current slide in its place and fades it in. A swipe that has moved the row meanwhile has brought its own slide.
     */
    fade() {
      const shown = this.slides[this.onScreen()];
      const { opacity } = getComputedStyle(shown);
      this.fading?.cancel();
      this.fading = shown.animate({ opacity: [opacity, 0] }, motionTokens());
      this.fading.onfinish = () => {
        this.moving = false;
        if (this.slides[this.onScreen()] !== shown) return;
        this.jump(this.index);
        this.fading = this.slides[this.index].animate({ opacity: [0, 1] }, motionTokens());
      };
    }

    /** After a swipe (or a smooth scroll) comes to rest, the slide it came to becomes the current one. */
    settle() {
      this.moving = false;
      const index = this.onScreen();
      if (index !== this.index && this.slides[index]) {
        this.stop();
        this.index = index;
        this.update();
        this.restartClock();
      }
    }

    update() {
      this.slides.forEach((slide, position) => {
        slide.inert = position !== this.index;
        slide.toggleAttribute('data-current', position === this.index);
      });
      this.pages.forEach((page, position) => {
        if (position === this.index) page.setAttribute('aria-current', 'true');
        else page.removeAttribute('aria-current');
      });
      this.counters.forEach((counter) => (counter.textContent = String(this.index + 1)));
    }

    // ---- Input ----

    onClick(event) {
      this.stop();
      const step = event.target.closest('[data-step]');
      if (step) return this.go(this.index + Number(step.dataset.step));
      const page = event.target.closest('[data-page]');
      if (page) this.go(Number(page.dataset.page));
    }

    onKey(event) {
      if (event.target !== this.viewport || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowRight') this.go(this.index + 1);
      else if (event.key === 'ArrowLeft') this.go(this.index - 1);
      else return;
      this.stop();
      event.preventDefault();
    }

    // ---- Rotation ----

    startRotation(signal) {
      this.setPaused(lessMotion.matches);
      this.timer.addEventListener(
        'animationend',
        (event) => {
          if (event.target === this.timer) this.go(this.index + 1);
        },
        { signal }
      );

      // The mouse anywhere over the section holds the rotation, and so does focus inside the rotator.
      const section = this.closest('.shopify-section') || this;
      section.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.hold('pointer'), {
        signal,
      });
      section.addEventListener('pointerleave', () => this.release('pointer'), { signal });
      this.addEventListener('focusin', () => this.hold('focus'), { signal });
      this.addEventListener('focusout', (event) => !this.contains(event.relatedTarget) && this.release('focus'), {
        signal,
      });
      document.addEventListener('visibilitychange', () => this.sync('tab', document.hidden), { signal });
      this.observer = new IntersectionObserver((entries) => {
        this.sync('screen', !entries[entries.length - 1].isIntersecting);
      });
      this.observer.observe(this);
      lessMotion.addEventListener('change', () => lessMotion.matches && this.setPaused(true), { signal });
    }

    /** The shopper has taken the slides in hand: the rotation pauses, and the slide they came to stays. */
    stop() {
      if (this.dataset.run) this.setPaused(true);
    }

    /** Rotates the slides, or keeps them still. */
    setPaused(paused) {
      const state = paused ? 'paused' : 'playing';
      this.dataset.run = state;
      if (this.pager) this.pager.dataset.run = state;
      // A moving rotator is not announced at every change; a still one is.
      this.viewport.setAttribute('aria-live', paused ? 'polite' : 'off');
    }

    hold(reason) {
      this.holds.add(reason);
      this.syncHold();
    }

    release(reason) {
      this.holds.delete(reason);
      this.syncHold();
    }

    sync(reason, on) {
      if (on) this.hold(reason);
      else this.release(reason);
    }

    syncHold() {
      const held = this.holds.size > 0;
      this.toggleAttribute('data-hold', held);
      this.pager?.toggleAttribute('data-hold', held);
    }

    /** Starts the slide's time again from its full length. */
    restartClock() {
      if (!this.timer || this.dataset.run !== 'playing') return;
      const fill = this.querySelector('.pager__mark[aria-current="true"] .pager__fill');
      // Web Animations API: rewinding a running CSS animation to its start.
      for (const element of [this.timer, fill]) {
        for (const animation of element?.getAnimations() ?? []) animation.currentTime = 0;
      }
    }

    // ---- Theme editor ----

    listenToEditor(signal) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.slides.indexOf(event.target.closest('.slide-rotator__slide'));
          if (index < 0) return;
          this.hold('editor');
          this.go(index, { instant: true });
        },
        { signal }
      );
      this.addEventListener('shopify:block:deselect', () => this.release('editor'), { signal });
    }
  }

  customElements.define('slide-rotator', SlideRotator);
}
